/**
 * The public signing page's API calls (jobq-api "SHIPMENT SIGNING", /portal/sign/*).
 *
 * The token in the URL is the only credential: /portal/sign/ is API-key-exempt, so these are bare
 * fetches to REACT_APP_API_BASE_URL (config.API_BASE_URL's value; config.js's wrapper, when the app
 * has loaded it, still adds the public X-API-Key header and the API ignores it there).
 * ⚠ Don't import config.js here: it pulls in axios, and SignPortal is also mounted inside the
 * operator's screen, whose tests would then have to mock axios (same rule as face/faceApi.js).
 * Every call throws a SignApiError: `status` 0 = no connection (the phone can retry with the SAME
 * client_request_id), otherwise the HTTP status; `message` is a sentence safe to show a customer.
 */
export class SignApiError extends Error {
  constructor(status, message, detail) {
    super(message);
    this.name = 'SignApiError';
    this.status = status;
    this.detail = detail;
  }

  /** No answer at all, or a gateway hiccup: worth trying again with the same request id. */
  get retryable() {
    return this.status === 0 || this.status === 502 || this.status === 504 || (this.status === 503 && !this.detail);
  }
}

function base() {
  return String(process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
}

function signPath(token, rest = '') {
  return `${base()}/portal/sign/${encodeURIComponent(String(token || ''))}${rest}`;
}

function detailText(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && !Array.isArray(detail)) return String(detail.message || detail.error || '');
  return '';
}

async function call(url, { method = 'GET', body } = {}) {
  let res;
  try {
    res = await fetch(url, {
      method,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new SignApiError(0, 'No connection. Check your signal and try again.');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new SignApiError(res.status, detailText(data && data.detail) || `Error ${res.status}`, data && data.detail);
  }
  return data;
}

/** GET /portal/sign/{token}: what the phone may show (order, pallets, what to ask). */
export function getSignRequest(token) {
  return call(signPath(token));
}

/** POST /portal/sign/{token}/sign. Same client_request_id on a retry replays the stored result. */
export function submitSignature(token, body) {
  return call(signPath(token, '/sign'), { method: 'POST', body });
}

/** POST /portal/sign/{token}/photos -> {upload_id, upload_url, headers}. */
export function requestPhotoUpload(token, { filename, content_type, bytes }) {
  return call(signPath(token, '/photos'), { method: 'POST', body: { filename, content_type, bytes } });
}

/** POST /portal/sign/{token}/photos/{id}/confirm -> {upload_id, status, thumb_url}. */
export function confirmPhotoUpload(token, uploadId) {
  return call(signPath(token, `/photos/${encodeURIComponent(uploadId)}/confirm`), { method: 'POST', body: {} });
}

/** PUT the photo straight to storage with the presigned URL (no API key, no JSON). */
export async function putPhoto(uploadUrl, blob, headers) {
  let res;
  try {
    res = await fetch(uploadUrl, { method: 'PUT', headers: headers || { 'Content-Type': blob.type || 'image/jpeg' }, body: blob });
  } catch (e) {
    throw new SignApiError(0, 'No connection. The photo did not upload.');
  }
  if (!res.ok) throw new SignApiError(res.status, 'The photo did not upload.');
}

/** The customer's copy. The API answers a path ("/portal/sign/<token>/receipt.pdf"). */
export function receiptHref(token, receiptUrl) {
  const u = String(receiptUrl || '');
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${base()}${u}`;
  return signPath(token, '/receipt.pdf');
}

/** One id per screen: a retry after a dropped connection must reuse it. */
export function newClientRequestId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch (e) { /* older Safari: fall through */ }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** What the audit row keeps about the phone (no fingerprinting beyond this). */
export function deviceInfo(nav = typeof navigator !== 'undefined' ? navigator : {}, scr = typeof window !== 'undefined' ? window.screen : null) {
  const ua = String((nav && nav.userAgent) || '');
  let platform = 'Other';
  if (/iPhone/i.test(ua)) platform = 'iPhone';
  else if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && nav && nav.maxTouchPoints > 1)) platform = 'iPad';
  else if (/Android/i.test(ua)) platform = 'Android';
  else if (/Windows/i.test(ua)) platform = 'Windows';
  else if (/Macintosh/i.test(ua)) platform = 'Mac';
  let tz = null;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (e) { tz = null; }
  return {
    user_agent: ua.slice(0, 400),
    platform,
    screen: scr && scr.width ? `${scr.width}x${scr.height}` : null,
    tz,
  };
}
